import { useMemo } from 'react'
import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import type { components } from '@/api/schema'
import { API_BASE_URL, AuthError, apiFetch } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { QuoteBuilderApiError } from '@/features/quote-builder/api/client'

// Accounts receivable (USA-1094): customer invoices from quotes, for accounting.

type Schemas = components['schemas']

export type Receivable = Schemas['QbReceivableRead']
export type ReceivableDetail = Schemas['QbReceivableDetailRead']
export type ReceivablesSummary = Schemas['QbReceivablesSummary']
export type ReceivablesPage = Schemas['PaginatedResponse_QbReceivableRead_']
export type ReceivableStatusFilter = 'open' | 'overdue' | 'paid' | 'void' | 'all'
export type NewInvoiceDefaults = Schemas['QbNewInvoiceDefaults']
export type NewInvoice = Schemas['QbAdditionalInvoiceCreate']
export type InvoiceTax = Schemas['QbInvoiceTax']
export type Address = Schemas['QbQuoteAddress']
export type PoItemGroup = Schemas['QbPoItemGroup']
export type RefundOptions = Schemas['QbRefundOptions']
export type RefundOption = Schemas['QbRefundOption']
export type NewCredit = Schemas['QbInvoiceCreditCreate']
export type InvoiceEmails = Schemas['QbQuoteEmailsRead']

export type ReceivablesApi = <T>(path: string, init?: RequestInit) => Promise<T>

export function useReceivablesApi(): ReceivablesApi {
  const { getAccessToken } = useAuth()
  return useMemo(
    () =>
      async <T>(path: string, init: RequestInit = {}) => {
        const token = await getAccessToken()
        if (!token) throw new AuthError(401, 'Failed to acquire access token')
        const headers = new Headers(init.headers)
        headers.set('Authorization', `Bearer ${token}`)
        headers.set('Accept', 'application/json')
        if (init.body) headers.set('Content-Type', 'application/json')
        const response = await apiFetch(`${API_BASE_URL}/dashboard/receivables${path}`, { ...init, headers })
        if (!response.ok) throw new QuoteBuilderApiError(response.status, await response.json().catch(() => ({})))
        return (await response.json()) as T
      },
    [getAccessToken],
  )
}

export const receivablesKeys = {
  all: ['receivables'] as const,
  list: (status: ReceivableStatusFilter, search: string, page: number) =>
    ['receivables', 'list', status, search, page] as const,
  summary: ['receivables', 'summary'] as const,
  detail: (invoiceId: string) => ['receivables', 'detail', invoiceId] as const,
}

export const receivablesQueryOptions = (
  api: ReceivablesApi,
  status: ReceivableStatusFilter,
  search: string,
  page: number,
) =>
  queryOptions({
    queryKey: receivablesKeys.list(status, search, page),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        status,
        page: String(page),
        limit: '50',
      })
      if (search) params.set('search', search)
      return api<ReceivablesPage>(`?${params}`, { signal })
    },
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  })

export const receivablesSummaryQueryOptions = (api: ReceivablesApi) =>
  queryOptions({
    queryKey: receivablesKeys.summary,
    queryFn: ({ signal }) => api<ReceivablesSummary>('/summary', { signal }),
    refetchInterval: 60_000,
  })

export const receivableQueryOptions = (api: ReceivablesApi, invoiceId: string) =>
  queryOptions({
    queryKey: receivablesKeys.detail(invoiceId),
    queryFn: ({ signal }) => api<ReceivableDetail>(`/${invoiceId}`, { signal }),
    refetchInterval: 60_000,
  })

/** Where a new invoice starts: a project, a quote, a customer, or nothing (pick the customer). */
export type NewInvoiceSource = { projectUuid?: string; quoteId?: string; customerGroupId?: number }

export const newInvoiceDefaultsQueryOptions = (api: ReceivablesApi, source: NewInvoiceSource) =>
  queryOptions({
    queryKey: ['receivables', 'new-invoice', source.projectUuid ?? null, source.quoteId ?? null, source.customerGroupId ?? null],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams()
      if (source.projectUuid) params.set('project_uuid', source.projectUuid)
      if (source.quoteId) params.set('quote_id', source.quoteId)
      if (source.customerGroupId != null) params.set('customer_group_id', String(source.customerGroupId))
      return api<NewInvoiceDefaults>(`/new-invoice?${params}`, { signal })
    },
    staleTime: 0,
  })

/** The tax for a ship-to staff typed on an invoice without a quote (USA-1543): only the ZIP, state and country count. */
export const newInvoiceTaxQueryOptions = (api: ReceivablesApi, address: Pick<Address, 'postal_code' | 'state' | 'country'>) =>
  queryOptions({
    queryKey: ['receivables', 'new-invoice-tax', address.postal_code.trim(), address.state.trim(), address.country.trim()],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        postal_code: address.postal_code.trim(),
        state: address.state.trim(),
        country: address.country.trim() || 'US',
      })
      return api<InvoiceTax>(`/new-invoice/tax?${params}`, { signal })
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  })

/** The invoices money can come off (one invoice, or the project's) and the project's PO items. */
export type RefundSource = { projectUuid?: string; invoiceId?: string; quoteId?: string }

export const refundOptionsQueryOptions = (api: ReceivablesApi, source: RefundSource) =>
  queryOptions({
    queryKey: ['receivables', 'refund-options', source.projectUuid ?? null, source.invoiceId ?? null, source.quoteId ?? null],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams()
      if (source.projectUuid) params.set('project_uuid', source.projectUuid)
      if (source.invoiceId) params.set('invoice_id', source.invoiceId)
      if (source.quoteId) params.set('quote_id', source.quoteId)
      return api<RefundOptions>(`/refund-options?${params}`, { signal })
    },
    staleTime: 0,
  })

export const receivablesApi = {
  refund: (api: ReceivablesApi, credit: NewCredit) =>
    api<ReceivableDetail>('/credits', { method: 'POST', body: JSON.stringify(credit) }),
  create: (api: ReceivablesApi, invoice: NewInvoice) =>
    api<ReceivableDetail>('', { method: 'POST', body: JSON.stringify(invoice) }),
  send: (api: ReceivablesApi, invoiceId: string, requestId: string) =>
    api<ReceivableDetail>(`/${invoiceId}/send`, { method: 'POST', body: JSON.stringify({ request_id: requestId }) }),
}

/** What went wrong creating or sending an invoice, and what to do about it. */
export function newInvoiceErrorMessage(error: unknown) {
  if (!(error instanceof QuoteBuilderApiError)) return 'Could not confirm the invoice. Check AR Invoices before trying again.'
  const messages: Record<string, string> = {
    customer_required: 'Choose the customer to bill.',
    invoice_total_required: 'The total must be more than zero.',
    tax_review_required: 'Tax could not be worked out for the delivery address. Fix the address, or mark the invoice tax exempt.',
    customer_not_found: 'That customer no longer exists. Choose another.',
    stripe_invoice_failed: 'The invoice could not be created. Try again; it will not be created twice.',
    stripe_unavailable: 'Invoicing is not set up in this environment. Ask your administrator.',
    stripe_mode_mismatch: 'Invoicing is set up for the wrong account here. Ask your administrator.',
    stripe_access_denied: 'The invoice service refused the request. Ask your administrator to check its access.',
    stripe_invoice_send_failed: 'The invoice was created but not emailed. Send it from its page.',
    invoice_email_failed: 'The invoice was created but the email did not go out. Send it from its page.',
    invoice_not_open: 'This invoice is no longer open, so it was not emailed.',
    refund_exceeds_available: 'That is more than can come off this invoice.',
    invoice_not_refundable: 'Nothing more can come off this invoice.',
    stripe_refund_failed: 'The refund could not be issued. Try again; it will not be issued twice.',
  }
  if (error.status === 422 && !messages[error.code]) return 'Check the email address and every line, then try again.'
  return messages[error.code] || 'The invoice could not be created. Try again.'
}

/** "Overdue 12 days", "Due Oct 22", "Paid", in the words accounting uses. */
export function receivableState(row: Receivable): {
  label: string
  tone: 'danger' | 'warning' | 'progress' | 'success' | 'neutral'
} {
  if (row.status === 'paid') return { label: 'Paid', tone: 'success' }
  if (row.status === 'void') return { label: 'Void', tone: 'neutral' }
  if (row.status === 'uncollectible') return { label: 'Uncollectible', tone: 'danger' }
  if (row.payment_processing) return { label: 'Bank payment processing', tone: 'progress' }
  if (row.status === 'overdue') return { label: `${row.days_overdue} days overdue`, tone: 'danger' }
  if (!row.sent_at) return { label: 'Not sent', tone: 'warning' }
  return { label: 'Open', tone: 'progress' }
}

// Due dates and reminder days are calendar days in UTC, the way the reminder schedule counts them.
const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

export function formatDay(value: string | null | undefined) {
  if (!value) return '—'
  return day.format(new Date(value.length === 10 ? `${value}T12:00:00Z` : value))
}

const localDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/** The day something happened, in the viewer's time zone (emailed, paid). */
export function formatWhen(value: string | null | undefined) {
  return value ? localDay.format(new Date(value)) : '—'
}
